import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@lib/i18n';

const LanguageToggle: React.FC = () => {
  const { language, changeLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="language-toggle" ref={dropdownRef}>
      <button
        className="language-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Change language, current: ${currentLang?.name || language}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="language-flag" role="img" aria-label={currentLang?.name}>{currentLang?.flag}</span>
        <svg
          className="language-arrow"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          fill="currentColor"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <title>Dropdown arrow</title>
          <path
            fillRule="evenodd"
            d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="language-dropdown" role="menu" aria-label="Language options">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${language === lang.code ? 'active' : ''}`}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              role="menuitem"
              aria-label={`Switch to ${lang.name}`}
              aria-current={language === lang.code ? 'true' : 'false'}
            >
              <span className="language-flag" role="img" aria-label={lang.name}>{lang.flag}</span>
              <span className="language-name">{lang.nativeName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
